/**
 * JotRx Setup Banner
 *
 * Dismissible promotional banner on the encounter page advertising JotRx e-prescribing.
 * Shown to users who do NOT yet have the JotRx entitlement. Users who already have
 * JotRx see the setup Callout/Card components on settings pages instead.
 *
 * When the JotRx bundle is not yet available for purchase, only the "Book a setup call"
 * button is shown. The setup CTA and subtitle appear once the bundle is purchasable.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { GUIDE_SURFACES } from '@features/knowledge-base/links';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import MedicationIcon from '@mui/icons-material/Medication';
import { alpha, Box, Button, IconButton, keyframes, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useStatsigClient } from '@statsig/react-bindings';

import { useJotRxSetupState } from '../hooks/useJotRxSetupState';

import { JotRxGuideLink } from './JotRxGuideLink';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4); }
  70% { box-shadow: 0 0 0 8px rgba(25, 118, 210, 0); }
  100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0); }
`;

const STORAGE_KEY = 'jotrx_setup_banner_dismissed';
const DYNAMIC_CONFIG = 'jotrx_setup_config';

function getSubtitle(setupState: string): string {
  switch (setupState) {
    case 'idp_complete':
      return 'Identity verified! Open DoseSpot to set up two-factor authentication (TFA) and start prescribing.';
    case 'registered':
      return 'Almost there! Complete identity proofing in DoseSpot to start writing prescriptions.';
    case 'practice_connected':
      return 'Complete your clinician setup to start writing prescriptions.';
    default:
      return 'Connect your practice to DoseSpot to start e-prescribing from the same place you take notes.';
  }
}

export function JotRxSetupBanner() {
  const { client } = useStatsigClient();
  const { setupState, isLoading, isFullySetup, isJotRxPurchasable } = useJotRxSetupState();
  const { jotrx } = useEventTracking();

  const { setupCallUrl, setupCtaUrl } = useMemo(() => {
    const config = client.getDynamicConfig(DYNAMIC_CONFIG);
    const value = config.value as { setup_call_url?: string; setup_cta_url?: string };
    return {
      setupCallUrl: value.setup_call_url || '',
      setupCtaUrl: value.setup_cta_url || '',
    };
  }, [client]);

  const [dismissedForState, setDismissedForState] = useState(
    () => localStorage.getItem(STORAGE_KEY) || '',
  );

  const isDismissed = dismissedForState === setupState;
  const isVisible = !isFullySetup && !isDismissed && !isLoading;

  useEffect(() => {
    if (isVisible) {
      jotrx.bannerShown({ setup_state: setupState });
    }
  }, [isVisible, jotrx, setupState]);

  const dismiss = useCallback(() => {
    setDismissedForState(setupState);
    localStorage.setItem(STORAGE_KEY, setupState);
  }, [setupState]);

  const handleDismiss = useCallback(() => {
    dismiss();
    jotrx.bannerDismissed({ setup_state: setupState });
  }, [dismiss, jotrx, setupState]);

  const handleCtaClick = useCallback(() => {
    if (!setupCtaUrl) return;
    jotrx.demoRequested({ source: 'recorder_banner', setup_state: setupState });
    window.open(setupCtaUrl, '_blank', 'noopener,noreferrer');
  }, [jotrx, setupState, setupCtaUrl]);

  const handleBookCall = useCallback(() => {
    if (!setupCallUrl) return;
    jotrx.setupCallBooked({ source: 'recorder_banner' });
    window.open(setupCallUrl, '_blank', 'noopener,noreferrer');
  }, [jotrx, setupCallUrl]);

  if (!isVisible) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2.5,
        py: 1.5,
        borderBottom: '1px solid',
        borderBottomColor: theme => alpha(theme.palette.primary.main, 0.15),
        bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: '50%',
          bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
          flexShrink: 0,
        }}
      >
        <MedicationIcon sx={{ fontSize: 18, color: 'primary.main' }} />
      </Box>

      {/* Copy */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
          You can now prescribe directly from JotPsych
        </Typography>
        {isJotRxPurchasable && (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            {getSubtitle(setupState)}
          </Typography>
        )}
      </Box>

      {/* CTAs, with the guide link beneath them */}
      <Stack
        spacing={0.5}
        sx={{ alignItems: 'flex-end', flexShrink: 0, pr: 3, typography: 'caption' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {setupCtaUrl && (
            <Button
              variant="contained"
              size="small"
              endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
              onClick={handleCtaClick}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                borderRadius: 1.5,
                px: 2,
                py: 0.75,
                animation: `${pulse} 2s ease-in-out infinite`,
              }}
            >
              Get a demo
            </Button>
          )}
          {setupCallUrl && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CalendarMonthIcon sx={{ fontSize: '14px !important' }} />}
              onClick={handleBookCall}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                borderRadius: 1.5,
                px: 2,
                py: 0.75,
                borderColor: 'divider',
                color: 'text.primary',
                bgcolor: 'background.paper',
                whiteSpace: 'nowrap',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'divider',
                },
              }}
            >
              Book a setup call
            </Button>
          )}
        </Box>
        <JotRxGuideLink surfaceId={GUIDE_SURFACES.JOTRX_BANNER} />
      </Stack>

      {/* Dismiss */}
      <IconButton
        size="small"
        onClick={handleDismiss}
        aria-label="Dismiss banner"
        sx={{
          position: 'absolute',
          top: 6,
          right: 6,
          p: 0.5,
          color: 'text.secondary',
          '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
}
